import { AxiosError } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';

import { api } from '@/api';
import { API_DOMAIN } from '@/constants';
import { apiUrl } from '@/env';
import { IPlugsCheckerTaskCreate, IPlugsCheckerTaskUpdate } from '@/interfaces/plugs-checker-tasks';
import { delay } from '@/utils';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';
import { State } from '../state';
import { readOnePlugsCheckerTask } from './getters';
import {
  commitSetPlugsCheckerTask, commitSetPlugsCheckerTaskIsDownloading, commitSetPlugsCheckerTasks,
  commitSetPlugsCheckerTasksStat, commitSetPlugsCheckerTaskStatus,
} from './mutations';
import { PlugsCheckerTaskState } from './state';
import { authHeaders, downloadFile, withDefaultValue } from '../store-utils';

type MainContext = ActionContext<PlugsCheckerTaskState, State>;

const MAX_FAILED_ATTEMPTS = 2 * 60; // 60 minutes
const ATTEMPTS_INTERVAL = 30 * 1000;  // 30 seconds
const API_URL = apiUrl + API_DOMAIN;
const DOWNLOAD_ONCE = true;

const utils = {
  counter: withDefaultValue({}),  // to control the number of failed attempts

  async downloadPlugsCheckerTaskResult(context: MainContext, payload: { id: number, result: string }, once = false) {
    const url = `${API_URL}/plugs-checker/${payload.id}/result`;
    const { token } = context.rootState.main;
    const { id, result } = payload;

    const updateStatuses = () => {
      commitSetPlugsCheckerTaskIsDownloading(context, { id, isDownloading: false});
      commitSetPlugsCheckerTaskStatus(context, { id, status: 'ENDED' });
    };

    if (once) {
      commitSetPlugsCheckerTaskIsDownloading(context, { id, isDownloading: true });
      commitSetPlugsCheckerTaskStatus(context, { id, status: 'DOWNLOAD' });
    } else {
      const updatedTask = readOnePlugsCheckerTask(context)(id);
      if (updatedTask?.status !== 'EXPORT' && !updatedTask?.result?.endsWith('.zip')) {
        delete this.counter[id];
        return;
      }
    }

    // на случай, если захотим брать название файла отчта из store
    // const result = context.state.se_tasks.find((YtTask => YtTask.id === payload.id))?.result;
    // console.log('task.result --> ', result);

    try {
      const response = await fetch(url, authHeaders(token));
      if (response.status !== 200) throw new Error(`Response status: ${response.status}`);
      const bytes = await response.blob();
      delete this.counter[id];
      downloadFile(bytes, result);
      commitAddNotification(context, { content: 'Файл отчета успешно загружен', color: 'success' });
      updateStatuses();
    } catch (error) {
      if (once) {
        // console.log('Ошибка загрузки файла:', error);
        commitAddNotification(context, {
          content: 'Ошибка загрузки файла',
          color: 'error',
        });
        updateStatuses();
      } else if (this.counter[id] < MAX_FAILED_ATTEMPTS) {
          setTimeout(() => this.downloadPlugsCheckerTaskResult(context, payload), ATTEMPTS_INTERVAL);
          this.counter[id]++;
        } else {
          delete this.counter[id];
          commitAddNotification(context, {
            content: 'Превышено максимальное количество попыток скачивания файла. Операция отменена',
            color: 'error' });
          updateStatuses();
        }
    }
  },
};


export const actions = {
  async actionGetPlugsCheckerTasks(context: MainContext) {
    try {
      const { data } = await api.getPlugsCheckerTasks(context.rootState.main.token);
      commitSetPlugsCheckerTasks(context, data);
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionUpdatePlugsCheckerTask(context: MainContext, payload: { id: number, task: IPlugsCheckerTaskUpdate }) {
    const { token } = context.rootState.main;
    const { id, task } = payload;
    try {
      const loadingNotification = { content: 'Сохранение', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const [{ data }] = await Promise.all([api.updatePlugsCheckerTask(token, id, task), delay(500)]);
      commitSetPlugsCheckerTask(context, data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно обновлена', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionCreatePlugsCheckerTask(context: MainContext, payload: IPlugsCheckerTaskCreate) {
    const { token } = context.rootState.main;
    try {
      const loadingNotification = { content: 'Создание', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const [{ data }] = await Promise.all([api.createPlugsCheckerTask(token, payload), delay(500)]);
      commitSetPlugsCheckerTask(context, data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно создана', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionDeletePlugsCheckerTask(context: MainContext, payload: { id: number }) {
    const { token } = context.rootState.main;
    try {
      const loadingNotification = { content: 'Удаление', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const [{ data }] = await Promise.all([api.deletePlugsCheckerTask(token, payload.id), delay(500)]);
      commitSetPlugsCheckerTask(context, data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно удалена', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionRunPlugsCheckerTask(context: MainContext, payload: { id: number }) {
    const { token } = context.rootState.main;
    const { id } = payload;
    try {
      const loadingNotification = { content: 'Запуск', showProgress: true };
      commitAddNotification(context, loadingNotification);
      await Promise.all([api.runPlugsCheckerTask(token, id), delay(500)]);
      commitSetPlugsCheckerTaskStatus(context, { id, status: 'ACTIVE' });
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно запущена', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionKillPlugsCheckerTask(context: MainContext, payload: { id: number }) {
    const { token } = context.rootState.main;
    try {
      const loadingNotification = { content: 'Прерывание', showProgress: true };
      commitAddNotification(context, loadingNotification);
      const [{ data }] = await Promise.all([api.killPlugsCheckerTask(token, payload.id), delay(500)]);
      commitSetPlugsCheckerTask(context, data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: 'Задача успешно прервана', color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  async actionStatPlugsCheckerTasks(context: MainContext) {
    const { token } = context.rootState.main;
    try {
      const { data } = await api.statPlugsCheckerTasks(token);
      commitSetPlugsCheckerTasksStat(context, data);
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },

  // загружаем файл отчета
  async actionDownloadPlugsCheckerTaskResult(context: MainContext, payload: { id: number, result: string }) {
    utils.downloadPlugsCheckerTaskResult(context, payload, DOWNLOAD_ONCE);
  },

  async actionDownloadPlugsCheckerTaskFile(context: MainContext, payload: { taskFile: string }) {
    const { taskFile } = payload;
    const url = `${API_URL}/plugs-checker/task-file/${taskFile}`;
    const { token } = context.rootState.main;

    try {
      const response = await fetch(url, authHeaders(token));
      if (response.status !== 200) throw new Error(`Response status: ${response.status}`);
      const bytes = await response.blob();
      downloadFile(bytes, taskFile);
      commitAddNotification(context, { content: 'Файл отчета успешно загружен', color: 'success' });
    } catch (error) {
        commitAddNotification(context, {
          content: 'Ошибка загрузки файла',
          color: 'error',
        });
    }

  },
};

const { dispatch } = getStoreAccessors<PlugsCheckerTaskState, State>('');

export const dispatchCreatePlugsCheckerTask = dispatch(actions.actionCreatePlugsCheckerTask);
export const dispatchGetPlugsCheckerTasks = dispatch(actions.actionGetPlugsCheckerTasks);
export const dispatchUpdatePlugsCheckerTask = dispatch(actions.actionUpdatePlugsCheckerTask);
export const dispatchDeletePlugsCheckerTask = dispatch(actions.actionDeletePlugsCheckerTask);
export const dispatchRunPlugsCheckerTask = dispatch(actions.actionRunPlugsCheckerTask);
export const dispatchKillPlugsCheckerTask = dispatch(actions.actionKillPlugsCheckerTask);
export const dispatchStatPlugsCheckerTasks = dispatch(actions.actionStatPlugsCheckerTasks);
export const dispatchDownloadPlugsCheckerTaskResult = dispatch(actions.actionDownloadPlugsCheckerTaskResult);
export const dispatchDownloadPlugsCheckerTaskFile = dispatch(actions.actionDownloadPlugsCheckerTaskFile);
